import React, {FC, useMemo} from 'react';
import {css, FormControl, IconButton, MenuItem, Select, useTheme} from '@mui/material';
import {GetStylesFunction} from '@wallaby-ui/core';
import {SelectChangeEvent} from '@mui/material/Select/SelectInput';
import {PickersCalendarHeaderProps} from '@mui/x-date-pickers/PickersCalendarHeader/PickersCalendarHeader.types';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface OwnProps extends PickersCalendarHeaderProps<Date> {
  value: Date | null;
  onChange: (value: Date | null) => void;
  enableFuture?: boolean;
  boundMinDate?: Date | null;
  boundMaxDate?: Date | null;
}

type Props = OwnProps;

const DEFAULT_MIN_DATE = new Date(1900, 0, 1);
const DEFAULT_MAX_DATE = new Date(2099, 11, 31);

export const FilterNodeTypeDateMonthRangeCalendarHeader: FC<Props> = (props: Props) => {
  const {enableFuture, currentMonth, views, value, onChange, minDate, maxDate, boundMinDate, boundMaxDate} = props;

  const theme = useTheme();
  const muiStyles = getStyles({theme});

  const today = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);
  const effectiveMinDate = boundMinDate ?? minDate ?? DEFAULT_MIN_DATE;
  const effectiveMaxDate = boundMaxDate ?? maxDate ?? (enableFuture ? DEFAULT_MAX_DATE : today);
  const minYear = effectiveMinDate.getFullYear();
  const maxYear = effectiveMaxDate.getFullYear();

  const getYearsRange = (start: number, stop: number, step: number) =>
    Array.from({length: Math.floor((stop - start) / step) + 1}, (_, i) => start + i * step);
  const years = getYearsRange(minYear, maxYear, 1);

  const updateDate = (newValue: Date | null) => {
    onChange(newValue);
  };

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    const year = Number(event.target.value);
    value?.setFullYear(year);
    updateDate(value);
    event.stopPropagation();
    event.preventDefault();
  };

  const handleSelectPreviousYear = () => {
    const previousYear = new Date(currentMonth);

    previousYear.setFullYear(previousYear.getFullYear() - 1);

    updateDate(previousYear);
  };

  const handleSelectNextYear = () => {
    const futureYear = new Date(currentMonth);

    futureYear.setFullYear(futureYear.getFullYear() + 1);

    updateDate(futureYear);
  };

  return (
    <div css={muiStyles.calendarHeaderWrapper}>
      <IconButton onClick={handleSelectPreviousYear} disabled={currentMonth.getFullYear() === minYear}>
        <ChevronLeftIcon />
      </IconButton>
      <div css={muiStyles.selectWrapper}>
        {views.includes('year') && (
          <FormControl size='small'>
            <Select
              css={muiStyles.select}
              value={currentMonth.getFullYear()}
              renderValue={(selected) => selected}
              label='Year'
              onChange={handleYearChange}
              MenuProps={{
                PaperProps: {sx: {maxHeight: 300}},
              }}
            >
              {years.map((year, index) => (
                <MenuItem key={index} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>
      <IconButton onClick={handleSelectNextYear} disabled={currentMonth.getFullYear() === maxYear}>
        <ChevronRightIcon />
      </IconButton>
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  calendarHeaderWrapper: css`
    display: flex;
    justify-content: space-around;
    align-items: center;
  `,
  selectWrapper: css`
    display: flex;
    gap: ${options?.theme?.spacing(2)};
  `,
  select: css`
    padding-top: ${options?.theme?.spacing(0.5)} !important;
    padding-left: ${options?.theme?.spacing(5)} !important;

    svg {
      right: 18px !important;
    }
  `,
});
